import { yupResolver } from '@hookform/resolvers/yup';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject } from 'mobx-react';
import React, { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';
import { toast } from 'sonner';
import { v4 as uuid } from 'uuid';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useSurveyPermissions } from '../../../../models/hooks/useSurveyPermissions';
import { copyToClipboard, createRichContentEditorStateWithText } from '../../../../models/UiUtils';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../../components-v2/Drawer/AppDrawer';
import { Page } from '../../../components-v2/PageLayout';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '../../../components/Breadcrumb';
import { FabContext } from '../../../components/FabContext';
import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { QRCodeModal } from '../../../components/QRCodeModal';
import { useClonableSurvey } from '../../../hooks/useClonableSurvey';
import { DefaultUniversalAssistantChat } from '../../templates/AssistantChat';
import { ReorderQuestionsModalComponent } from '../shared/components/ReorderQuestionsModal/ReorderQuestionsModal';
import { SurveyActions } from '../shared/components/SurveyActions';
import { SurveyHeader } from '../shared/components/SurveyHeader';
import { TemplatePreviewNotice } from '../shared/components/TemplatePreviewNotice';
import { UnsavedChangesWarning } from '../shared/components/UnsavedChangesWarning';
import { PreviewModeProvider } from '../shared/contexts/PreviewModeContext';
import { useUnsavedChanges } from '../shared/hooks/useUnsavedChanges';
import { hasCustomFormChanges } from '../shared/utils/customFormComparison';
import { BasicInfoSettings, ConfirmationSettings, DateRangeSettings, SurveyTabs } from './components';
import { useCustomSurveyFormInitialization, useCustomSurveyMutations, useCustomSurveyValidation } from './hooks';
import { styleSheet } from './styles';
import { CustomSurveyFormData, TabValues, validationSchema } from './utils';

interface CustomSurveyProps extends IImpersonationContextComponentProps {
	className?: string;
	/**
	 * When true, component operates in template creation/editing mode:
	 * - Hides QR Code button (templates don't have survey links)
	 * - Changes Save button to "Save as Template" (or "Update Template" if isEditingTemplate=true)
	 * - Prevents default survey save behavior
	 * - Calls onSaveAsTemplate callback instead of creating survey
	 */
	templateMode?: boolean;
	/**
	 * When true (along with templateMode), changes button text to "Update Template" instead of "Save as Template"
	 */
	isEditingTemplate?: boolean;
	/**
	 * When true, renders form in read-only preview mode:
	 * - All form inputs are disabled
	 * - Save button is hidden
	 * - Only Clone/Duplicate action is available
	 * Used for customer-facing template preview
	 */
	isPreviewOnly?: boolean;
	/**
	 * Template ID when editing an existing template in templateMode.
	 * Required for operations like reordering fields on existing templates.
	 */
	templateId?: string;
	/**
	 * Callback invoked when saving as template (only used in templateMode)
	 * Receives the complete survey definition that should be saved as a template
	 */
	onSaveAsTemplate?: (surveyDefinition: Api.ICustomSurvey) => void;
	/**
	 * Optional initial survey definition to load into the form.
	 * When provided, bypasses useClonableSurvey and uses this data directly.
	 * Used by admin template editing to load template surveyDefinition.
	 */
	initialSurveyDefinition?: Api.ICustomSurvey;
}

interface IEditSurveyComponent {
	BasicInformationComponent: React.FC<{ survey: Api.ICustomSurvey; account: Api.IAccount }>;
	DateRangeSettings: React.FC<{ survey: Api.ICustomSurvey }>;
	ConfirmationSettings: React.FC<{ survey: Api.ICustomSurvey }>;
}

export const CustomSurveyBase: React.FC<CustomSurveyProps> & Partial<IEditSurveyComponent> = ({
	impersonationContext,
	templateMode = false,
	isEditingTemplate = false,
	isPreviewOnly = false,
	templateId,
	onSaveAsTemplate,
	initialSurveyDefinition,
}) => {
	const [showEditSurveyName, setShowEditSurveyName] = useState(false);
	const [showReorderModal, setShowReorderModal] = useState(false);
	const [isQRModalOpen, setIsQRModalOpen] = useState(false);
	const [clearedVisibilityFieldIds, setClearedVisibilityFieldIds] = useState<string[]>();
	const [reorderDirtyFieldIds, setReorderDirtyFieldIds] = useState<string[]>();
	const userSession = useUserSession();
	const surveyPermissions = useSurveyPermissions();
	const account = userSession?.account.toJs();

	const [selectedTab, setSelectedTab] = useState<TabValues>(isPreviewOnly ? TabValues.Preview : TabValues.Settings);
	const params = useParams<{ id?: string }>();
	const eventId = params?.id;

	/**
	 * Data source determination:
	 *
	 * Template Mode (initialSurveyDefinition provided):
	 *   - Admin is creating/editing a template, data passed directly as prop
	 *   - Skip backend fetch by passing undefined to useClonableSurvey
	 *   - isClone forced to false because templates use different save flow (callback)
	 *   - isEdit based on whether surveyDefinition has ID (edit existing vs create new)
	 *
	 * Normal Mode (no initialSurveyDefinition):
	 *   - Load survey from backend using eventId from URL
	 *   - isClone/isEdit determined by useClonableSurvey based on URL params
	 */
	const clonableSurveyResult = useClonableSurvey<Api.ICustomSurvey>(
		initialSurveyDefinition ? undefined : eventId,
		impersonationContext
	);

	const surveyData = initialSurveyDefinition || clonableSurveyResult.surveyData;
	const isClone = initialSurveyDefinition ? false : clonableSurveyResult.isClone;
	const isEdit = initialSurveyDefinition ? Boolean(initialSurveyDefinition.id) : clonableSurveyResult.isEdit;
	const isSurveyLoading = clonableSurveyResult.isLoading;

	const handleCopyToClipboard = () => {
		copyToClipboard(surveyData?.anonymousLink);
		toast.success('Link copied to clipboard');
	};

	const onQRCodeClick = () => {
		setIsQRModalOpen(true);
	};

	const handleQRModalClose = () => {
		setIsQRModalOpen(false);
	};

	const methods = useForm<CustomSurveyFormData>({
		defaultValues: {
			name: '',
			startDate: new Date().toISOString(),
			expirationDate: undefined,
			options: {
				contactNameOptions: { collect: true, require: true, updateExisting: false },
				contactEmailOptions: { collect: true, require: true, updateExisting: false },
				contactPhoneNumberOptions: { collect: true, require: true, updateExisting: false },
				contactBirthdayOptions: { collect: true, require: true, updateExisting: false },
				contactAddressOptions: { collect: true, require: true, updateExisting: false },
				intro: createRichContentEditorStateWithText(''),
				companyName: account?.companyName || '',
				sendConfirmationMessage: false,
				includeCompanyLogo: true,
				confirmationMessage: createRichContentEditorStateWithText(''),
			},
			customForm: {
				name: '',
				fields: [
					{
						fieldType: Api.FormFieldType.String,
						id: uuid(),
						label: '',
						isHidden: false,
						isOptional: false,
						options: [],
					},
				],
			},
			hasDateRange: false,
			updateExistingContacts: false,
			notificationSettings: {
				isEnabled: true,
				additionalRecipients: [],
			},
			tagGenerationSettings: {
				tagOnCompletion: false,
				completionTag: '',
				customFormFieldTagSettings: [],
			},
		},
		resolver: yupResolver(validationSchema),
		mode: 'onSubmit',
		reValidateMode: 'onSubmit',
	});
	const { getValues, handleSubmit, setValue, formState, reset } = methods;
	const formValues = getValues();

	const { updateSurveyMutation, createSurveyMutation } = useCustomSurveyMutations({
		surveyData,
		impersonationContext,
		getValues,
		reset,
		templateMode,
		templateId,
	});

	// Initialize form with survey data
	useCustomSurveyFormInitialization({
		surveyData,
		isClone,
		setValue,
	});

	// Track if we just finished loading to ignore false positives from HtmlEditor initialization
	const [isInitializing, setIsInitializing] = React.useState(true);
	const hasResetForSurvey = React.useRef<string | null>(null);

	React.useEffect(() => {
		const surveyId = surveyData?.id;
		if (!isSurveyLoading && surveyData && hasResetForSurvey.current !== surveyId) {
			// Mark that we're handling this survey to prevent duplicate resets
			hasResetForSurvey.current = surveyId;

			// Give HtmlEditor time to finish its initialization before considering changes
			// 1000ms timeout is based on manual testing showing HtmlEditor (TinyMCE) typically
			// initializes within 500-800ms. The 1000ms value provides headroom for slower machines.
			// Future improvement: Use HtmlEditor's onLoad callback instead of timeout (requires refactoring form components)
			const timer = setTimeout(() => {
				// Reset the form with current values to clear dirty state without changing values
				// This updates both the values and default values, marking everything as clean
				const currentValues = getValues();
				reset(currentValues);
				setIsInitializing(false);
			}, 1000);
			return () => clearTimeout(timer);
		}
	}, [isSurveyLoading, surveyData, getValues, reset]);

	// Handle validation errors
	useCustomSurveyValidation({
		formState,
		setShowEditSurveyName,
	});

	// Track if customForm fields have meaningful changes by comparing specific properties
	// Don't compare while survey is loading to avoid false positives from comparing
	// undefined survey data with default form values
	const customFormFieldsHaveChanges = React.useMemo(() => {
		if (isSurveyLoading) {
			return false;
		}
		return hasCustomFormChanges(surveyData?.customForm?.fields, formValues.customForm?.fields);
	}, [isSurveyLoading, surveyData?.customForm?.fields, formValues.customForm?.fields]);

	// Track unsaved changes
	const hasUnsavedChanges = useUnsavedChanges({
		surveyType: 'custom',
		isEdit,
		isClone,
		isInitializing,
		formState,
		additionalConditions: [customFormFieldsHaveChanges],
	});

	const handleToggleEditName = () => {
		setShowEditSurveyName(!showEditSurveyName);
	};

	const handleNameChange = (name: string) => {
		setValue('name', name);
	};

	const handleReorderQuestions = () => {
		setShowReorderModal(true);
	};

	const handleReorderModalClose = useCallback(
		(result?: { reorderedQuestions?: any; clearedVisibilityFieldIds?: string[] }, cancel?: boolean) => {
			setShowReorderModal(false);
			if (!cancel && result?.reorderedQuestions) {
				// Update customForm fields to the new order locally (user must click Save to persist)
				const currentFields = getValues().customForm?.fields || [];
				const currentFieldsMap = new Map(currentFields.map(field => [field.id, field]));
				const reorderedCompleteFields = result.reorderedQuestions
					.map((field: Api.IFormField<string>) => currentFieldsMap.get(field.id))
					.filter(Boolean) as Api.IFormField<string>[];

				// Determine which fields changed position so they can be marked dirty
				const originalIndexMap = new Map(currentFields.map((f, i) => [f.id, i]));
				const dirtyFromReorder = reorderedCompleteFields
					.filter((field, newIndex) => originalIndexMap.get(field.id) !== newIndex)
					.map(field => field.id);

				setValue('customForm.fields', reorderedCompleteFields);

				// Mark reordered fields as dirty (includes cleared visibility fields)
				const allDirtyIds = new Set(dirtyFromReorder);
				if (result.clearedVisibilityFieldIds?.length) {
					for (const id of result.clearedVisibilityFieldIds) {
						allDirtyIds.add(id);
					}
					setClearedVisibilityFieldIds(result.clearedVisibilityFieldIds);
				}
				if (allDirtyIds.size > 0) {
					setReorderDirtyFieldIds(Array.from(allDirtyIds));
				}
			}
		},
		[getValues, setValue]
	);

	// Helper function to convert rich text content to raw format
	const convertRichTextContent = (values: CustomSurveyFormData) => ({
		...values.options,
		intro: values.options?.intro?.getRawRichTextContent?.(),
		confirmationMessage: values.options?.confirmationMessage?.getRawRichTextContent?.(),
	});

	// Helper function to create notification settings
	const createNotificationSettings = (values: CustomSurveyFormData) => ({
		_type: 'SurveyNotificationSettings' as const,
		...(values.notificationSettings || {
			isEnabled: false,
			additionalRecipients: [],
		}),
		additionalRecipients: (values.notificationSettings?.additionalRecipients ?? []).filter(
			recipient => recipient && recipient.trim() !== ''
		),
	});

	// Helper function to create custom form
	const createCustomForm = (values: CustomSurveyFormData) => ({
		...values.customForm,
		name: values.name,
	});

	// Strip validation-only fields that shouldn't be sent to the API
	const stripValidationFields = (values: CustomSurveyFormData) => {
		const { surveyFormVisibilityValidation: _, ...rest } = values;
		return rest;
	};

	// Ensure surveyForm.isEnabled is always true when saving a custom survey,
	// regardless of whether the Questions tab was loaded during this session.
	const ensureSurveyFormEnabled = (values: CustomSurveyFormData) =>
		values.surveyForm ? { ...values.surveyForm, isEnabled: true } : values.surveyForm;

	// Helper function to create cloned survey
	const createClonedSurvey = (values: CustomSurveyFormData): Api.ICustomSurvey => ({
		_type: 'CustomSurvey',
		...stripValidationFields(values),
		notificationSettings: createNotificationSettings(values),
		options: convertRichTextContent(values),
		customForm: createCustomForm(values),
		tagGenerationSettings: values.tagGenerationSettings,
		surveyForm: ensureSurveyFormEnabled(values),
		id: surveyData.id,
	});

	// Helper function to create edited survey
	const createEditedSurvey = (values: CustomSurveyFormData): Api.ICustomSurvey => ({
		...surveyData,
		...stripValidationFields(values),
		options: convertRichTextContent(values),
		customForm: createCustomForm(values),
		notificationSettings: createNotificationSettings(values),
		tagGenerationSettings: values.tagGenerationSettings,
		surveyForm: ensureSurveyFormEnabled(values),
		id: surveyData.id,
	});

	// Helper function to create new survey
	const createNewSurvey = (values: CustomSurveyFormData): Api.ICustomSurvey => ({
		_type: 'CustomSurvey',
		...stripValidationFields(values),
		options: convertRichTextContent(values),
		customForm: createCustomForm(values),
		notificationSettings: {
			_type: 'SurveyNotificationSettings',
			...values.notificationSettings,
		},
		tagGenerationSettings: values.tagGenerationSettings,
		surveyForm: ensureSurveyFormEnabled(values),
	});

	// Helper function to handle survey update
	const handleSurveyUpdate = (survey: Api.ICustomSurvey) => {
		updateSurveyMutation.mutate({ survey });
	};

	// Helper function to handle survey creation
	const handleSurveyCreation = (survey: Api.ICustomSurvey) => {
		createSurveyMutation.mutate({ survey });
	};

	/**
	 * Handles form submission with different behaviors based on mode and state:
	 *
	 * Template Mode (templateMode=true):
	 *   - Admin template editing: Calls onSaveAsTemplate callback instead of API mutation
	 *   - Used by both create and edit template flows in admin portal
	 *   - Parent component handles saving to backend
	 *
	 * Normal Survey Mode (templateMode=false):
	 *   - isEdit + isClone: User is cloning an existing survey
	 *     Creates new survey with new ID but copies all settings from original
	 *     Derived from URL query param (?clone=<id>)
	 *   - isEdit + !isClone: User is editing their existing survey
	 *     Updates survey in place, preserves original ID
	 *   - !isEdit: User is creating a brand new survey from scratch
	 *     Creates new survey with new ID and default settings
	 */
	const onSubmit = () => {
		const values = getValues();

		// Template mode: Call callback with survey definition instead of saving
		if (templateMode && onSaveAsTemplate) {
			const surveyDefinition = isEdit ? createEditedSurvey(values) : createNewSurvey(values);
			onSaveAsTemplate(surveyDefinition);
			return;
		}

		// Normal survey mode: Create or update survey
		if (isEdit) {
			const editSurvey = isClone ? createClonedSurvey(values) : createEditedSurvey(values);
			handleSurveyUpdate(editSurvey);
			return;
		}

		const newSurvey = createNewSurvey(values);
		handleSurveyCreation(newSurvey);
	};
	const breadcrumbPageTitle = useCallback(() => {
		if (isClone) {
			return 'Clone Survey';
		}
		return params.id ? (surveyData?.name ?? 'Edit Survey') : 'New Custom Survey';
	}, [params.id, surveyData?.name, isClone]);

	const settingsTabError = React.useMemo(() => {
		// Only show errors if the form has been submitted at least once
		if (!formState.isSubmitted) {
			return false;
		}
		return (
			Object.keys(formState?.errors.notificationSettings || {})?.length > 0 ||
			Object.keys(formState?.errors.tagGenerationSettings || {})?.length > 0
		);
	}, [formState.errors, formState.isSubmitted]);

	const questionsTabError = React.useMemo(() => {
		// Only show errors if the form has been submitted at least once
		if (!formState.isSubmitted) {
			return false;
		}
		return (
			Object.keys(formState?.errors?.customForm?.fields || {})?.length > 0 ||
			Object.keys((formState?.errors as any)?.surveyFormVisibilityValidation || {})?.length > 0
		);
	}, [formState.errors, formState.isSubmitted]);

	const isAnyMutationLoading = createSurveyMutation.isPending || updateSurveyMutation.isPending;

	const questions = formValues?.customForm?.fields || [];
	const hasMultipleQuestions = questions.length > 1;

	return (
		<Page styles={[styleSheet.page]}>
			<div className={css(styleSheet.container)}>
				<MultiContainerHeader
					appBarHeader={
						<Breadcrumb>
							<BreadcrumbList>
								<BreadcrumbItem>
									<BreadcrumbLink asChild>
										<NavLink to='/surveys/custom'>Surveys</NavLink>
									</BreadcrumbLink>
								</BreadcrumbItem>
								<BreadcrumbSeparator />
								<BreadcrumbItem>
									<BreadcrumbPage>{breadcrumbPageTitle()}</BreadcrumbPage>
								</BreadcrumbItem>
							</BreadcrumbList>
						</Breadcrumb>
					}
				/>
				<FabContext appearance={{ hidden: true }} />
				<FormProvider {...methods}>
					<PreviewModeProvider isPreviewOnly={isPreviewOnly}>
						<form data-testid='form' onSubmit={handleSubmit(onSubmit)}>
							<header data-testid='header' className={css(styleSheet.header)}>
								<SurveyHeader
									surveyName={formValues.name || 'Untitled Survey'}
									showEditName={showEditSurveyName}
									onEditToggle={handleToggleEditName}
									onNameChange={handleNameChange}
									validationError={formState.errors?.name?.message}
									isEdit={isEdit}
								/>
								<SurveyActions
									onCopyLink={handleCopyToClipboard}
									isLoading={isAnyMutationLoading}
									isSaveDisabled={showEditSurveyName}
									showCopyLink={!templateMode && !!surveyData?.id}
									surveyLink={surveyData?.anonymousLink}
									onQRCodeClick={templateMode ? undefined : onQRCodeClick}
									showViewReport={!!surveyData?.id && surveyPermissions.canViewReportingCustomSurvey()}
									surveyId={surveyData?.id}
									surveyType='CustomSurvey'
									saveText={templateMode ? (isEditingTemplate ? 'Update Template' : 'Duplicate Survey') : 'Save'}
								/>
							</header>
							{isPreviewOnly ? (
								<TemplatePreviewNotice />
							) : (
								<UnsavedChangesWarning hasUnsavedChanges={hasUnsavedChanges && !isSurveyLoading} />
							)}
							<SurveyTabs
								selectedTab={selectedTab}
								onTabChange={setSelectedTab}
								settingsTabError={settingsTabError}
								questionsTabError={questionsTabError}
								surveyData={surveyData}
								account={account}
								isEdit={isEdit}
								isClone={isClone}
								onReorderQuestions={handleReorderQuestions}
								hasMultipleQuestions={hasMultipleQuestions}
								isLoading={isAnyMutationLoading}
								templateMode={templateMode}
								clearedVisibilityFieldIds={clearedVisibilityFieldIds}
								reorderDirtyFieldIds={reorderDirtyFieldIds}
							/>
						</form>
					</PreviewModeProvider>
				</FormProvider>
			</div>
			<AppDrawer>
				<StickyAppDrawerContent value='ai-assistant'>
					<DefaultUniversalAssistantChat />
				</StickyAppDrawerContent>
				<AppDrawerToolbar>
					<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
				</AppDrawerToolbar>
			</AppDrawer>
			<ReorderQuestionsModalComponent
				modalProps={{
					isOpen: showReorderModal,
					onRequestClose: handleReorderModalClose,
				}}
				questions={questions}
				surveyId={eventId || ''}
				impersonationContext={impersonationContext}
				surveyForm={formValues.surveyForm}
			/>
			<QRCodeModal
				modalProps={{
					isOpen: isQRModalOpen,
					onRequestClose: handleQRModalClose,
				}}
				url={surveyData?.anonymousLink || ''}
				title='QR Code'
				filename={`survey-qr-${surveyData?.id || 'code'}`}
			/>
		</Page>
	);
};

export const CustomSurvey = inject(ImpersonationContextKey)(CustomSurveyBase);

CustomSurvey.BasicInformationComponent = BasicInfoSettings;
CustomSurvey.DateRangeSettings = DateRangeSettings;
CustomSurvey.ConfirmationSettings = ConfirmationSettings;
